import React, { useState, useRef, useEffect } from 'react';
import { createCompleteQuestion, uploadQuestionImg, fetchQuestionDetails, updateQuestionWithAnswers } from '../../../api/quizeApi';
import { AlertCircle, X, PlusCircle, XIcon, Loader2, ImageIcon } from 'lucide-react';

const UpdateQuestion = ({ questionId, quizId, handleCloseUpdateModal, setRefreshFlag }) => {
  const [questionData, setQuestionData] = useState({
    question_text: '',
    points: 1,
    isPanelty: false,
    answers: [],
    question_img: null
  });
  const [formSubmitting, setFormSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [imageUploading, setImageUploading] = useState(false);
  const fileInputRef = useRef(null);
  const [isLoading, setIsLoading] = useState(true);

  // Fetch question details on component mount
  useEffect(() => {
    const loadQuestionDetails = async () => {
      try {
        setIsLoading(true);
        const response = await fetchQuestionDetails(questionId);
        
        // Console log the details for verification
        console.log('Question Details:', response);

        // Transform the API response to match the component's state structure
        const transformedData = {
          question_text: response.question.question_text,
          points: response.question.points, // Default points, adjust if needed
          isPanelty: response.question.isPenalty === 1,
          question_img: response.question.question_img,
          answers: response.answers.map(answer => ({
            answer_text: answer.answer_text,
            is_correct: answer.is_correct === 1,
            answer_id: answer.answer_id // Keep original answer ID for potential updates
          }))
        };

        setQuestionData(transformedData);
        
        // Set image preview if exists
        if (response.question.question_img) {
          setImagePreview(response.question.question_img);
        }
      } catch (err) {
        setError('Failed to load question details');
        console.error(err);
      } finally {
        setIsLoading(false);
      }
    };

    loadQuestionDetails();
  }, [questionId]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setQuestionData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleAnswerChange = (index, field, value) => {
    const updatedAnswers = [...questionData.answers];
    updatedAnswers[index] = {
      ...updatedAnswers[index],
      [field]: value
    };
    
    setQuestionData(prev => ({
      ...prev,
      answers: updatedAnswers
    }));
  };

  const handleImageChange = async (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file type and size
      const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
      const maxSize = 5 * 1024 * 1024; // 5MB

      if (!allowedTypes.includes(file.type)) {
        setError('Invalid file type. Please upload a JPEG, PNG, GIF, or WebP image.');
        return;
      }

      if (file.size > maxSize) {
        setError('Image size should be less than 5MB.');
        return;
      }

      try {
        // Start image upload
        setImageUploading(true);
        setError(null);

        // Upload image
        const imgURL = await uploadQuestionImg(file);

        // Create a preview
        const reader = new FileReader();
        reader.onload = () => {
          setImagePreview(reader.result);
        };
        reader.readAsDataURL(file);

        // Update state with uploaded image URL
        setQuestionData(prev => ({ 
          ...prev, 
          question_img: imgURL 
        }));
      } catch (uploadError) {
        console.error('Image upload failed:', uploadError);
        setError('Failed to upload image. Please try again.');
        setImagePreview(null);
      } finally {
        setImageUploading(false);
      }
    }
  };

  const handleRemoveImage = () => {
    setImagePreview(null);
    setQuestionData(prev => ({ ...prev, question_img: null }));
    
    // Reset file input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleAddAnswer = () => {
    if (questionData.answers.length >= 6) {
      setError('Maximum of 6 answers allowed');
      return;
    }
    
    setQuestionData(prev => ({
      ...prev,
      answers: [...prev.answers, { answer_text: '', is_correct: false }]
    }));
  };

  const handleRemoveAnswer = (index) => {
    if (questionData.answers.length <= 2) {
      setError("A question must have at least 2 options");
      return;
    }
    
    const updatedAnswers = [...questionData.answers];
    updatedAnswers.splice(index, 1);
    
    setQuestionData(prev => ({
      ...prev,
      answers: updatedAnswers
    }));
  };

  const handleSubmitQuestion = async (e) => {

    e.preventDefault();
    setFormSubmitting(true);
    setError(null);
    
    try {
      // Validate at least one correct answer
      const hasCorrectAnswer = questionData.answers.some(answer => answer.is_correct);
      if (!hasCorrectAnswer) {
        throw new Error("Please mark at least one option as correct");
      }
      
      // Create a copy of the question data for submission
      const updateQuestionData = { 
        ...questionData,
        quizId: quizId,
        questionId: questionId
      };

      console.log('Update Question Data:', updateQuestionData);
      await updateQuestionWithAnswers(updateQuestionData);

      handleCloseUpdateModal();
      setRefreshFlag(prev => !prev);
      console.log('Question updated successfully');
      // TODO: Implement update API call
      // await updateCompleteQuestion(updateQuestionData);
      
      // Optionally close modal or show success message
      // handleCloseUpdateModal();
    } catch (err) {
      setError(err.message || "Failed to update question");
    } finally {
      setFormSubmitting(false);
    }
  };

  // Show loading state
  if (isLoading) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="flex items-center text-white">
          <Loader2 className="w-8 h-8 mr-3 animate-spin" />
          Loading question details...
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 overflow-y-auto">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-3xl max-h-[90vh] flex flex-col">
        <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
          <h2 className="text-xl font-semibold text-gray-800">Update Question</h2>
          <button
            onClick={handleCloseUpdateModal}
            className="text-gray-500 hover:text-gray-700 focus:outline-none"
          >
            <X className="w-6 h-6" />
          </button>
        </div>
        
        <div className="p-6 flex-grow overflow-y-auto">
          {error && (
            <div className="flex items-center p-4 mb-4 text-sm bg-red-50 border border-red-200 rounded-md text-red-800">
              <AlertCircle className="w-5 h-5 mr-2 flex-shrink-0" />
              <span>{error}</span>
            </div>
          )}
          
          <form onSubmit={handleSubmitQuestion}>
            <div className="space-y-6">
              {/* Question Text */}
              <div className="flex items-center gap-6 mb-4">
                <div className="min-w-48">
                  <label
                    htmlFor="points"
                    className="block text-sm font-medium text-gray-700 mb-1"
                  >
                    Points*
                  </label>
                  <input
                    type="number"
                    id="points"
                    name="points"
                    value={questionData.points}
                    onChange={handleInputChange}
                    min={1}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div className="flex items-center h-full pt-6">
                  <input
                    type="checkbox"
                    id="isPanelty"
                    name="isPanelty"
                    checked={questionData.isPanelty}
                    onChange={(e) => {
                      const updatedValue = {
                        ...questionData,
                        isPanelty: e.target.checked,
                      };
                      setQuestionData(updatedValue);
                    }}
                    className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500 cursor-pointer"
                  />
                  <label
                    htmlFor="isPanelty"
                    className="ml-2 text-sm font-medium text-gray-700 cursor-pointer"
                  >
                    Is Penalty?
                  </label>
                </div>
              </div>
              <div>
                <label htmlFor="question_text" className="block text-sm font-medium text-gray-700">
                  Question Text*
                </label>
                <textarea
                  id="question_text"
                  name="question_text"
                  rows={3}
                  className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                  placeholder="Enter your question here"
                  value={questionData.question_text}
                  onChange={handleInputChange}
                  required
                />
              </div>
              
              {/* Question Image */}
              <div>
                <label htmlFor="question_img" className="block text-sm font-medium text-gray-700">
                  Question Image (Optional)
                </label>
                <div className="mt-1 flex items-center space-x-3">
                  <input
                    type="file"
                    id="question_img"
                    name="question_img"
                    ref={fileInputRef}
                    accept="image/jpeg,image/png,image/gif,image/webp"
                    className="hidden"
                    onChange={handleImageChange}
                    disabled={imageUploading}
                  />
                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    className="flex items-center px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                    disabled={imageUploading}
                  >
                    <ImageIcon className="w-4 h-4 mr-2" />
                    {imageUploading ? 'Uploading...' : 'Choose Image'}
                  </button>
                  <span className="text-xs text-gray-500">
                    Max 5MB (JPEG, PNG, GIF, WebP)
                  </span>
                </div>

                {imageUploading && (
                  <div className="mt-2 flex items-center text-blue-600">
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    <span>Uploading image...</span>
                  </div>
                )}

                {imagePreview && (
                  <div className="mt-2 flex items-center space-x-3">
                    <img 
                      src={imagePreview} 
                      alt="Question preview" 
                      className="h-40 w-auto rounded-md object-contain bg-gray-50 p-2"
                    />
                    <button
                      type="button"
                      className="text-sm text-red-600 hover:text-red-800 flex items-center"
                      onClick={handleRemoveImage}
                      disabled={imageUploading}
                    >
                      <XIcon className="w-4 h-4 mr-1" />
                      Remove
                    </button>
                  </div>
                )}
              </div>
              
              {/* Answers Section */}
              <div>
                <div className="flex justify-between items-center mb-2">
                  <h3 className="text-sm font-medium text-gray-700">Answers</h3>
                  <button
                    type="button"
                    className="text-xs flex items-center text-blue-600 hover:text-blue-800 disabled:opacity-50 disabled:cursor-not-allowed"
                    onClick={handleAddAnswer}
                    disabled={questionData.answers.length >= 6}
                  >
                    <PlusCircle className="w-3 h-3 mr-1" />
                    Add Answer
                  </button>
                </div>
                
                <div className="space-y-3">
                  {questionData.answers.map((answer, index) => (
                    <div key={index} className="flex items-start space-x-3">
                      <div className="flex-grow">
                        <input
                          type="text"
                          placeholder={`Answer ${index + 1}`}
                          className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                          value={answer.answer_text}
                          onChange={(e) => handleAnswerChange(index, 'answer_text', e.target.value)}
                          required
                        />
                      </div>
                      
                      <div className="flex items-center space-x-2">
                        <label className="flex items-center text-sm text-gray-700">
                          <input
                            type="radio"
                            name="correctAnswer"
                            checked={answer.is_correct}
                            onChange={() => {
                              const updatedAnswers = questionData.answers.map((a, i) => ({
                                ...a,
                                is_correct: i === index
                              }));
                              setQuestionData(prev => ({ ...prev, answers: updatedAnswers }));
                            }}
                            className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
                          />
                          <span className="ml-1">Correct</span>
                        </label>
                        
                        <button
                          type="button"
                          onClick={() => handleRemoveAnswer(index)}
                          className="text-gray-400 hover:text-red-600"
                        >
                          <XIcon className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
                
                <p className="mt-2 text-xs text-gray-500">
                  Tip: Make sure to mark at least one answer as correct.
                </p>
              </div>
            </div>
            
            <div className="mt-8 flex justify-end space-x-3">
              <button
                type="button"
                onClick={handleCloseUpdateModal}
                className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
                disabled={formSubmitting}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="px-4 py-2 bg-blue-600 rounded-md text-sm font-medium text-white hover:bg-blue-700 flex items-center"
                disabled={formSubmitting || imageUploading}
              >
                {formSubmitting ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Saving...
                  </>
                ) : (
                  'Update Question'
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default UpdateQuestion;